import { type HeadingLevel } from "@/types/gc-modules-types"

const LEVELS = ["h1", "h2", "h3", "h4", "h5", "h6"] as const
const LEVEL_SET: ReadonlySet<string> = new Set(LEVELS)

/** Sanity returns `null` for an unset dropdown, so CMS levels must be validated before use. */
export function isHeadingLevel(value: unknown): value is HeadingLevel {
    return typeof value === "string" && LEVEL_SET.has(value)
}

export function headingLevelRank(level: HeadingLevel): number {
    return LEVELS.indexOf(level) + 1
}

export function headingLevelFromRank(rank: number): HeadingLevel {
    const clamped = Math.min(6, Math.max(1, rank))
    return LEVELS[clamped - 1]
}

export function nextHeadingLevel(level: HeadingLevel): HeadingLevel {
    return headingLevelFromRank(headingLevelRank(level) + 1)
}

/** Body copy under a section title starts one level down; otherwise at h2. */
export function bodyHeadingLevel(hasSectionHeading: boolean): HeadingLevel {
    return hasSectionHeading ? "h3" : "h2"
}

/**
 * Map an author-picked heading onto a page outline.
 * The lowest author heading in the block becomes `baseLevel`; deeper
 * author levels stay relative. Visual size is unchanged (caller keeps style).
 */
export function remapAuthorHeadingLevel(
    authorLevel: HeadingLevel,
    minAuthorLevel: HeadingLevel,
    baseLevel: HeadingLevel
): HeadingLevel {
    const offset = headingLevelRank(authorLevel) - headingLevelRank(minAuthorLevel)
    return headingLevelFromRank(headingLevelRank(baseLevel) + offset)
}
